import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Container,
    Stack,
    Chip,
    Link,
    Button,
    Popover,
    TextField,
    List,
    ListItem,
    ListItemText,
    Checkbox,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Business, BusinessTableRow } from '../../models/Business';
import { SharedTable, Column } from '../../components/shared/table/Table';
import { getAllBusinesses } from '../../services/businessService';
import { StyledChip } from '../../components/shared/StyledTable';

type FilterType = 'all' | 'with_lead' | 'without_lead';

export const BusinessesComponent: React.FC = () => {
    const navigate = useNavigate();
    const [businesses, setBusinesses] = useState<BusinessTableRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [orderBy, setOrderBy] = useState<string>('createdAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedFilter, setSelectedFilter] = useState<FilterType>('all');
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [locationFilterAnchor, setLocationFilterAnchor] = useState<null | HTMLElement>(null);
    const [locationSearchQuery, setLocationSearchQuery] = useState('');

    const handleFilterChange = (filter: FilterType) => {
        setSelectedFilter(filter);
        setPage(0);
    };

    const columns: Column<BusinessTableRow>[] = [
        {
            id: 'createdAt',
            label: 'Created',
            width: 180,
            sortable: true,
            render: (row) => row.createdAt.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },
        {
            id: 'name',
            label: 'Name',
            width: 300,
            render: (row) => (
                <Box>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {row.name}
                    </Typography>
                    {row.website && (
                        <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
                            <Link href={row.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                {row.website}
                            </Link>
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            id: 'location',
            label: 'Location',
            width: 200,
            render: (row) => (row.city && row.state ? `${row.city}, ${row.state}` : 'Unknown')
        },
        {
            id: 'industry',
            label: 'Industry',
            width: 200,
            render: (row) => row.industryType || 'Unknown'
        },
    ];

    useEffect(() => {
        const loadBusinesses = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getAllBusinesses();
                console.log('Fetched businesses:', data); // Debug log

                // Transform Business[] to BusinessTableRow[] with proper null checks
                const businessRows = data.map(business => ({
                    ...business,
                    // Ensure required fields have default values
                    name: business.name || 'Unnamed Business',
                    city: business.city || '',
                    state: business.state || '',
                    industryType: business.industryType || '',
                    website: business.website || '',
                    hasLead: !!business.leadId,
                    // Ensure dates are properly handled
                    createdAt: business.createdAt instanceof Date ? business.createdAt : new Date(business.createdAt),
                    updatedAt: business.updatedAt instanceof Date ? business.updatedAt : new Date(business.updatedAt)
                }));

                console.log('Transformed businesses:', businessRows); // Debug log
                setBusinesses(businessRows);
            } catch (err) {
                console.error('Error loading businesses:', err);
                setError('Failed to load businesses. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadBusinesses();
    }, []);

    // Get unique locations from all businesses
    const availableLocations = useMemo(() => {
        const locations = new Set<string>();
        businesses.forEach(business => {
            if (business.city && business.state) {
                locations.add(`${business.city}, ${business.state}`);
            }
        });
        return Array.from(locations).sort();
    }, [businesses]);

    // Filter locations based on search
    const filteredLocations = useMemo(() => {
        if (!locationSearchQuery) return availableLocations;
        return availableLocations.filter(loc =>
            loc.toLowerCase().includes(locationSearchQuery.toLowerCase())
        );
    }, [availableLocations, locationSearchQuery]);

    const handleLocationFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setLocationFilterAnchor(event.currentTarget);
    };

    const handleLocationFilterClose = () => {
        setLocationFilterAnchor(null);
    };

    const handleLocationToggle = (location: string) => {
        setSelectedLocations(prev =>
            prev.includes(location)
                ? prev.filter(l => l !== location)
                : [...prev, location]
        );
    };

    const clearLocationFilters = () => {
        setSelectedLocations([]);
    };

    // Update the filtered businesses logic
    const filteredBusinesses = businesses.filter(business => {
        // Apply lead status filter
        if (selectedFilter === 'with_lead' && !business.hasLead) return false;
        if (selectedFilter === 'without_lead' && business.hasLead) return false;

        // Apply location filter
        if (selectedLocations.length > 0) {
            const businessLocation = business.city && business.state ? `${business.city}, ${business.state}` : '';
            if (!selectedLocations.includes(businessLocation)) return false;
        }

        // Apply search
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            return (
                business.name?.toLowerCase().includes(searchLower) ||
                business.city?.toLowerCase().includes(searchLower) ||
                business.state?.toLowerCase().includes(searchLower) ||
                business.industryType?.toLowerCase().includes(searchLower)
            );
        }
        return true;
    });

    const paginatedData = filteredBusinesses.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Business Management
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        onClick={handleLocationFilterClick}
                        color={selectedLocations.length > 0 ? "primary" : "inherit"}
                    >
                        Locations ({selectedLocations.length})
                    </Button>
                    <Chip
                        label="All"
                        color={selectedFilter === 'all' ? 'primary' : 'default'}
                        onClick={() => handleFilterChange('all')}
                        variant={selectedFilter === 'all' ? 'filled' : 'outlined'}
                    />
                    <Chip
                        label="With Lead"
                        color={selectedFilter === 'with_lead' ? 'primary' : 'default'}
                        onClick={() => handleFilterChange('with_lead')}
                        variant={selectedFilter === 'with_lead' ? 'filled' : 'outlined'}
                    />
                    <Chip
                        label="Without Lead"
                        color={selectedFilter === 'without_lead' ? 'primary' : 'default'}
                        onClick={() => handleFilterChange('without_lead')}
                        variant={selectedFilter === 'without_lead' ? 'filled' : 'outlined'}
                    />
                </Stack>

                <Popover
                    open={Boolean(locationFilterAnchor)}
                    anchorEl={locationFilterAnchor}
                    onClose={handleLocationFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 2, width: 300 }}>
                        <Stack spacing={2}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search locations..."
                                value={locationSearchQuery}
                                onChange={(e) => setLocationSearchQuery(e.target.value)}
                            />
                            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {filteredLocations.map((location) => (
                                    <ListItem
                                        key={location}
                                        dense
                                        button
                                        onClick={() => handleLocationToggle(location)}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={selectedLocations.includes(location)}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <ListItemText primary={location} />
                                    </ListItem>
                                ))}
                            </List>
                            {selectedLocations.length > 0 && (
                                <Button
                                    variant="text"
                                    onClick={clearLocationFilters}
                                    sx={{ alignSelf: 'flex-end' }}
                                >
                                    Clear All
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Popover>

                <SharedTable
                    columns={columns}
                    data={paginatedData}
                    loading={loading}
                    error={error}
                    orderBy={orderBy}
                    order={order}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={filteredBusinesses.length}
                    searchQuery={searchQuery}
                    searchPlaceholder="Search businesses..."
                    onSearchChange={setSearchQuery}
                    onRequestSort={(property) => {
                        const isAsc = orderBy === property && order === 'asc';
                        setOrder(isAsc ? 'desc' : 'asc');
                        setOrderBy(property);
                    }}
                    onPageChange={setPage}
                    onRowsPerPageChange={setRowsPerPage}
                    onRowClick={(row) => navigate(`/businesses/${row.id}`)}
                    getRowId={(row) => row.id}
                    emptyStateMessage="No businesses found."
                />
            </Box>
        </Container>
    );
}; 